import { useRef, useState } from 'react';
import { Link } from 'react-router';
import {
  ArrowClockwise,
  ArrowRight,
  BellRinging,
  CalendarBlank,
  Camera,
  CaretRight,
  Check,
  CheckCircle,
  ClipboardText,
  DeviceMobile,
  DotsThree,
  PlusSquare,
  Trophy,
  X,
} from '@phosphor-icons/react';
import { formatPoints, formatRecord, formatWinPercentage, type ResultCellDto } from '@pickem/shared';
import type {
  AvatarPanelProps,
  HistoryScreenProps,
  HomeScreenProps,
  InstallPromptProps,
  NotificationNudgeProps,
  LoginScreenProps,
  NotificationsPanelProps,
  PicksScreenProps,
  SettingsScreenProps,
  StandingsScreenProps,
  WeekResultsScreenProps,
} from '../../screens/contracts';
import { PickCard, formatKickoff } from '../PickCard';
import {
  Avatar,
  BrandMark,
  Button,
  Card,
  cx,
  EmptyState,
  ErrorState,
  Field,
  InlineNotice,
  Input,
  IosInstallSteps,
  LiveStatus,
  ResultPill,
  ScoreLine,
  SectionLabel,
  Skeleton,
  SkeletonList,
  Stat,
  TeamMark,
  WeekStrip,
} from '../ui';

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------

/**
 * Split composition rather than a centred card: the left rail carries the identity, the
 * right holds the form. On a phone the rail collapses to a compact header so the keyboard
 * never pushes the fields off screen.
 */
export function Login({
  mode,
  isSubmitting,
  error,
  notice,
  onSubmit,
  onModeChange,
  presetInviteCode,
  demoHint,
}: LoginScreenProps) {
  const [values, setValues] = useState({
    email: '',
    password: '',
    displayName: '',
    inviteCode: presetInviteCode ?? '',
    token: new URLSearchParams(window.location.search).get('token') ?? '',
  });

  const set = (key: keyof typeof values) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const copy = {
    login: { title: 'Sign in', cta: 'Sign in' },
    register: { title: 'Join the league', cta: 'Create account' },
    forgot: { title: 'Reset password', cta: 'Send reset link' },
    reset: { title: 'Choose a new password', cta: 'Update password' },
    join: { title: 'Join the league', cta: 'Join' },
  }[mode];

  return (
    <div className="taste-root grid min-h-[100dvh] grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
      {/* Identity rail */}
      <div className="relative flex flex-col justify-between overflow-hidden border-b border-line bg-field-950 px-6 py-8 lg:border-b-0 lg:border-r lg:px-14 lg:py-14">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 size-[420px] rounded-full opacity-[0.045]"
          style={{ background: 'radial-gradient(circle, #FF6B1A 0%, transparent 62%)' }}
        />
        <div className="flex items-center gap-2.5">
          <BrandMark className="size-8" />
          <span className="text-[15px] font-semibold tracking-tight">Otoe Pick&apos;em</span>
        </div>

        <div className="relative py-10 lg:py-0">
          <h1 className="max-w-[14ch] text-4xl font-semibold leading-[1.02] tracking-tighter text-field-100 lg:text-6xl">
            Five games.
            <br />
            <span className="text-field-500">Every week.</span>
          </h1>
          <p className="mt-5 max-w-[38ch] text-[15px] leading-relaxed text-field-400">
            Pick a side of every spread before kickoff. Lines are frozen when the week is
            published, so the number you picked is the number you are graded on.
          </p>
        </div>

        {/* The scoring breakdown used to sit here. It was answering a question nobody
            has before signing in, and the rules are on every screen once you are in.
            The spacer stays so the rail's justify-between composition still has three
            children and the headline keeps its position. */}
        <div aria-hidden />
      </div>

      {/* Form */}
      <div className="flex items-center px-6 py-10 lg:px-14">
        <form
          className="w-full max-w-sm"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(values);
          }}
        >
          <h2 className="text-2xl font-semibold tracking-tight">{copy.title}</h2>

          <div className="mt-7 flex flex-col gap-4">
            {mode === 'register' && (
              <Field label="Display name" hint="How you appear in the standings">
                <Input value={values.displayName} onChange={set('displayName')} autoComplete="nickname" required />
              </Field>
            )}

            {(mode === 'register' || mode === 'join') && (
              <Field label="Invite code" hint="From your commissioner">
                <Input
                  value={values.inviteCode}
                  onChange={set('inviteCode')}
                  // Shape only, never a real code. This placeholder used to spell out the
                  // league's actual invite, which anyone who reached the sign-up page could
                  // simply read and use — a private league with its key printed on the door.
                  placeholder="XXXX-XXXX"
                  autoCapitalize="characters"
                  required
                />
              </Field>
            )}

            {mode !== 'reset' && mode !== 'join' && (
              <Field label="Email">
                <Input type="email" value={values.email} onChange={set('email')} autoComplete="email" required />
              </Field>
            )}

            {(mode === 'login' || mode === 'register' || mode === 'reset') && (
              <Field
                label={mode === 'reset' ? 'New password' : 'Password'}
                hint={mode === 'register' || mode === 'reset' ? 'At least 8 characters' : undefined}
              >
                <Input
                  type="password"
                  value={values.password}
                  onChange={set('password')}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  required
                />
              </Field>
            )}
          </div>

          {error && (
            <div className="mt-4">
              <InlineNotice tone="error">{error}</InlineNotice>
            </div>
          )}
          {notice && (
            <div className="mt-4">
              <InlineNotice tone="success">{notice}</InlineNotice>
            </div>
          )}

          <Button type="submit" size="lg" full loading={isSubmitting} className="mt-6">
            {copy.cta}
            {!isSubmitting && <ArrowRight size={17} weight="bold" />}
          </Button>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            {mode === 'login' && (
              <>
                <button type="button" onClick={() => onModeChange('register')} className="text-signal hover:underline">
                  Create an account
                </button>
                <button type="button" onClick={() => onModeChange('forgot')} className="text-field-400 hover:text-field-200">
                  Forgot password
                </button>
              </>
            )}
            {mode !== 'login' && (
              <button type="button" onClick={() => onModeChange('login')} className="text-signal hover:underline">
                Back to sign in
              </button>
            )}
          </div>

          {demoHint && mode === 'login' && (
            <button
              type="button"
              onClick={() => setValues((v) => ({ ...v, email: demoHint.email, password: demoHint.password }))}
              className="mt-8 w-full rounded-xl border border-dashed border-line p-3 text-left transition-colors hover:border-line-strong"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-field-500">
                Demo account · tap to fill
              </div>
              <div className="mt-1.5 font-mono text-xs text-field-300">{demoHint.email}</div>
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Home
// ---------------------------------------------------------------------------

/**
 * The dashboard leads with the one thing the brief insists on: what you still have to do.
 * Rank and record sit *below* the call to action, never above it.
 */
export function Home({
  isLoading,
  isError,
  error,
  retry,
  home,
  countdown,
  picksHref,
  standingsHref,
  historyHref,
  commishHref,
  notificationNudge,
  installPrompt,
}: HomeScreenProps) {
  if (isError && error) return <ErrorState message={error} onRetry={retry} />;
  if (isLoading || !home) return <HomeSkeleton />;

  const week = home.week;
  const remaining = week ? week.requiredPicks - week.myPicksMade : 0;
  const complete = week?.allComplete ?? false;

  return (
    <div className="space-y-8">
      {/*
        Two prompts, and only ever one of them. Installing is the prerequisite on iOS —
        Safari will not grant notification permission to a page in a tab — so asking for
        notifications first would be asking for something the browser is going to refuse.
        The route layer decides which; this only has to not render both.
      */}
      {installPrompt ? (
        <InstallPrompt {...installPrompt} />
      ) : notificationNudge ? (
        <NotificationNudge {...notificationNudge} />
      ) : null}

      {/* The action ------------------------------------------------------- */}
      {week ? (
        <section className="taste-rise" style={{ '--index': 0 } as React.CSSProperties}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-field-500">
                {home.season.name}
              </div>
              <h1 className="mt-1.5 text-3xl font-semibold tracking-tighter text-field-100">
                {week.week.label}
              </h1>
            </div>
            {countdown && week.canPickAny && (
              <div className="shrink-0 text-right">
                <div className="font-mono text-xl font-semibold tabular-nums text-signal">
                  {countdown}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-field-500">
                  Until lock
                </div>
              </div>
            )}
          </div>

          <div
            className={cx(
              'mt-5 overflow-hidden rounded-card border',
              complete ? 'border-win/30 bg-win/[0.06]' : 'border-signal/30 bg-signal/[0.07]',
            )}
          >
            <div className="flex items-center gap-4 p-5">
              <div className="min-w-0 flex-1">
                <div
                  className={cx(
                    'text-lg font-semibold tracking-tight',
                    complete ? 'text-win' : 'text-field-100',
                  )}
                >
                  {complete
                    ? `You're all set for ${week.week.label}`
                    : week.week.slateStatus === 'draft'
                      ? 'This week is not out yet'
                      : `${remaining} pick${remaining === 1 ? '' : 's'} left to make`}
                </div>
                <div className="mt-1 font-mono text-[13px] tabular-nums text-field-400">
                  {week.myPicksMade} / {week.requiredPicks} picks made
                </div>
              </div>
              {complete ? (
                <span className="grid size-11 shrink-0 place-items-center rounded-full bg-win/15 text-win">
                  <Check size={22} weight="bold" />
                </span>
              ) : (
                <Link to={picksHref} className="shrink-0">
                  <Button size="lg">
                    Make picks
                    <ArrowRight size={17} weight="bold" />
                  </Button>
                </Link>
              )}
            </div>

            {/* Progress rail — thin, no boxed track, per the density guidance. */}
            <div className="h-0.5 w-full bg-field-800">
              <div
                className={cx('h-full transition-all duration-700 ease-[var(--ease-out-expo)]', complete ? 'bg-win' : 'bg-signal')}
                style={{
                  width: `${week.requiredPicks ? (week.myPicksMade / week.requiredPicks) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
        </section>
      ) : (
        <EmptyState
          title="No week yet"
          description="Your commissioner has not built a slate for this season. Once they publish one, it will show up here."
          icon={<CalendarBlank size={30} />}
        />
      )}

      {/* This week's games — inline, not buried ---------------------------- */}
      {week && week.games.length > 0 && (
        <section className="taste-rise space-y-3" style={{ '--index': 1 } as React.CSSProperties}>
          <SectionLabel
            action={
              <Link to={picksHref} className="flex items-center gap-1 text-[13px] text-signal hover:underline">
                Open picks
                <CaretRight size={13} weight="bold" />
              </Link>
            }
          >
            This week
          </SectionLabel>

          <Card className="divide-y divide-line">
            {week.games.map((game) => {
              const picked = game.myPick?.selection ?? null;
              const pickedSide = picked === 'away' ? game.away : picked === 'home' ? game.home : null;
              const live = game.live.phase === 'in' || game.live.phase === 'halftime';
              // "Over" per the contest, not only per the feed: a score the commissioner
              // typed in settles the pick before ESPN ever reports the game as final. A
              // voided game is explicitly not over — it can still be carrying a score from
              // before it was pulled, and showing that would invent a result nobody has.
              const over =
                game.live.phase === 'final' ||
                (game.result !== 'pending' && game.result !== 'void');
              // While the ball is in the air the scoreboard mirror is the fresher copy;
              // once a game is over the contest row is authoritative, and is the only copy
              // that exists when the score was entered by hand.
              const away = live
                ? game.live.awayScore
                : over
                  ? (game.away.score ?? game.live.awayScore)
                  : null;
              const home = live
                ? game.live.homeScore
                : over
                  ? (game.home.score ?? game.live.homeScore)
                  : null;
              // `detail` is only worth showing once a game is actually over — before
              // kickoff the feed puts the date in it, which the kickoff line already says.
              const statusLabel = over
                ? (game.live.phase === 'final' ? (game.live.detail ?? 'Final') : 'Final')
                : formatKickoff(game.kickoffAt);
              return (
                <div key={game.id} className="flex items-center gap-3 px-4 py-3">
                  <span className="w-4 shrink-0 font-mono text-[11px] text-field-600">{game.slot}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] text-field-200">
                      {game.away.team.shortName}{' '}
                      <span className="font-mono text-field-500">{game.away.lineLabel}</span>
                      <span className="mx-1.5 text-field-600">{game.neutralSite ? 'vs' : 'at'}</span>
                      {game.home.team.shortName}
                    </div>
                    {/*
                      Before kickoff this line answers "when"; once the ball is in the air
                      it answers "what is happening", which is the only question anybody
                      opens this screen to ask on a Sunday afternoon. The score reads
                      away-first, matching the order of the matchup directly above it.
                    */}
                    <div className="mt-0.5 flex items-center gap-2 font-mono text-[11px]">
                      {live ? (
                        <LiveStatus live={game.live} />
                      ) : (
                        <span className="truncate text-field-600">{statusLabel}</span>
                      )}
                      <ScoreLine
                        away={away}
                        home={home}
                        className={cx(
                          'shrink-0 font-medium',
                          live ? 'text-field-200' : 'text-field-400',
                        )}
                      />
                    </div>
                  </div>
                  {pickedSide ? (
                    <span
                      className={cx(
                        'shrink-0 rounded-md px-2 py-1 font-mono text-[11px] font-medium',
                        game.myPick?.result === 'win'
                          ? 'bg-win/15 text-win'
                          : game.myPick?.result === 'loss'
                            ? 'bg-loss/15 text-loss'
                            : game.myPick?.result === 'push'
                              ? 'bg-push/15 text-push'
                              : 'bg-signal/15 text-signal',
                      )}
                    >
                      {pickedSide.team.abbreviation}
                    </span>
                  ) : (
                    <span className="shrink-0 rounded-md border border-dashed border-line px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-field-600">
                      {game.locked ? 'Missed' : 'Open'}
                    </span>
                  )}
                </div>
              );
            })}
          </Card>
        </section>
      )}

      {/* Standing — below the action, deliberately ------------------------- */}
      <section className="taste-rise" style={{ '--index': 2 } as React.CSSProperties}>
        <SectionLabel
          action={
            <Link to={standingsHref} className="flex items-center gap-1 text-[13px] text-signal hover:underline">
              Full table
              <CaretRight size={13} weight="bold" />
            </Link>
          }
        >
          Where you stand
        </SectionLabel>

        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-4">
          <Stat
            value={home.me.tied ? `T${home.me.rank}` : `${home.me.rank}`}
            label={`of ${home.league.memberCount}`}
            accent
          />
          <Stat value={formatPoints(home.me.points)} label="Points" />
          <Stat value={formatRecord(home.me)} label="Record" />
          <Stat
            value={formatWinPercentage({ ...home.me })}
            label="Win pct"
          />
        </div>

        {/*
          Negative margin on the list, padding on each row: the highlight behind your own
          line still runs the full width, while the content sits in off the edges instead of
          being pressed against them.
        */}
        <div className="-mx-3 mt-6 divide-y divide-line border-t border-line sm:-mx-4">
          {home.standingsPreview.map((row) => (
            <div
              key={row.userId}
              className={cx(
                'flex items-center gap-3 px-3 py-2.5 sm:px-4',
                row.isMe && 'bg-signal/[0.05]',
              )}
            >
              <span className="w-6 shrink-0 font-mono text-xs tabular-nums text-field-500">
                {row.tied ? `T${row.rank}` : row.rank}
              </span>
              <Avatar
                userId={row.userId}
                displayName={row.displayName}
                url={row.avatarUrl}
                size={24}
              />
              <span className={cx('min-w-0 flex-1 truncate text-sm', row.isMe ? 'font-medium text-field-100' : 'text-field-300')}>
                {row.displayName}
                {row.isMe && <span className="ml-1.5 font-mono text-[10px] text-signal">YOU</span>}
              </span>
              <span className="shrink-0 font-mono text-xs tabular-nums text-field-500">
                {formatRecord(row)}
              </span>
              <span className="w-12 shrink-0 text-right font-mono text-sm font-semibold tabular-nums text-field-100">
                {formatPoints(row.points)}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Recent weeks ------------------------------------------------------ */}
      {home.recentWeeks.length > 0 && (
        <section className="taste-rise" style={{ '--index': 3 } as React.CSSProperties}>
          <SectionLabel
            action={
              <Link to={historyHref} className="flex items-center gap-1 text-[13px] text-signal hover:underline">
                History
                <CaretRight size={13} weight="bold" />
              </Link>
            }
          >
            Recent weeks
          </SectionLabel>
          <div className="taste-scroll-x -mx-4 mt-4 flex gap-2.5 px-4">
            {home.recentWeeks.map((week) => (
              <Card key={week.weekId} className="w-[136px] shrink-0 p-3.5">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-field-500">
                    {week.label}
                  </span>
                  {week.isWeeklyWinner && <Trophy size={14} weight="fill" className="text-push" />}
                </div>
                <div className="mt-3 font-mono text-2xl font-semibold tabular-nums">
                  {formatPoints(week.points)}
                </div>
                <div className="mt-1 font-mono text-[11px] text-field-500">
                  {formatRecord({ wins: week.wins, losses: week.losses, pushes: week.pushes })}
                  {week.rank > 0 && ` · #${week.rank}`}
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {commishHref && (
        <Link to={commishHref} className="block">
          <Card interactive className="flex items-center gap-3 p-4">
            <span className="grid size-9 place-items-center rounded-lg bg-field-800 text-field-300">
              <ClipboardText size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-field-100">Commissioner tools</div>
              <div className="text-[12px] text-field-500">Build the slate, grade games, manage the league</div>
            </div>
            <CaretRight size={16} className="shrink-0 text-field-500" />
          </Card>
        </Link>
      )}
    </div>
  );
}

/**
 * "Turn on notifications."
 *
 * Sits above the week because it is only ever shown to someone who cannot be reached any
 * other way — and the thing it is offering to fix is precisely that they will not hear
 * about the next slate.
 *
 * The button goes to Settings rather than requesting permission here. A permission prompt
 * fired from a card someone half-tapped is the fastest route to a permanent "denied",
 * which no amount of later UI can undo.
 */
function NotificationNudge({ settingsHref, onDismiss }: NotificationNudgeProps) {
  return (
    <section
      className="taste-rise relative overflow-hidden rounded-card border border-signal/30 bg-signal/[0.07] p-4"
      style={{ '--index': 0 } as React.CSSProperties}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-2 grid size-7 place-items-center rounded-lg text-field-500 transition-colors hover:bg-field-800 hover:text-field-300"
      >
        <X size={14} weight="bold" />
      </button>

      <div className="flex items-start gap-3 pr-8">
        <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-signal/15 text-signal">
          <BellRinging size={18} weight="fill" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-field-100">Get told when the slate drops</div>
          <p className="mt-1 text-[13px] leading-relaxed text-field-400">
            A nudge when a new week goes up and a reminder before kickoff if you still have
            picks to make. Nothing else.
          </p>
          <Link to={settingsHref} className="mt-3 inline-block">
            <Button size="sm">
              Turn on notifications
              <ArrowRight size={15} weight="bold" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

/**
 * "Add this to your home screen."
 *
 * Two mechanics behind one card, and they are not comparable. Chromium hands us an event
 * we can replay, so there is a button that genuinely installs the app. iOS Safari has no
 * such event and never will — the only route is a walk through Safari's own menus — so
 * there the honest thing is to describe the taps, with the actual icons, rather than offer
 * a button that would have to lie about what it does. Those steps come from
 * `IosInstallSteps`, shared with Settings so the two can never disagree.
 */
function InstallPrompt({
  onInstall,
  isIos,
  mentionNotifications,
  onDismiss,
}: InstallPromptProps) {
  return (
    <section
      className="taste-rise relative overflow-hidden rounded-card border border-line bg-field-850 p-4"
      style={{ '--index': 0 } as React.CSSProperties}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-2 grid size-7 place-items-center rounded-lg text-field-500 transition-colors hover:bg-field-800 hover:text-field-300"
      >
        <X size={14} weight="bold" />
      </button>

      <div className="flex items-start gap-3 pr-8">
        <BrandMark className="size-9" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-field-100">Add Pick&apos;em to your phone</div>
          <p className="mt-1 text-[13px] leading-relaxed text-field-400">
            It opens like an app — full screen, no address bar, one tap from your home
            screen.
            {mentionNotifications && ' On an iPhone it is also the only way to get notifications.'}
          </p>

          {onInstall ? (
            <Button size="sm" className="mt-3" onClick={onInstall}>
              <PlusSquare size={15} weight="bold" />
              Install
            </Button>
          ) : isIos ? (
            <div className="mt-3">
              <IosInstallSteps compact />
            </div>
          ) : (
            <ol className="mt-3 space-y-1.5 text-[13px] text-field-300">
              <li className="flex items-center gap-2">
                <span className="font-mono text-[11px] text-field-600">1</span>
                Open your browser&apos;s
                <DotsThree size={16} weight="bold" className="text-signal" />
                <span className="text-field-400">menu</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="font-mono text-[11px] text-field-600">2</span>
                <span className="font-medium">Install</span>
                <span className="text-field-400">or</span>
                <span className="font-medium">Add to Home Screen</span>
              </li>
            </ol>
          )}
        </div>
      </div>
    </section>
  );
}

function HomeSkeleton() {
  return (
    <div className="space-y-8">
      <div>
        <Skeleton className="h-3 w-28" />
        <Skeleton className="mt-2.5 h-8 w-32" />
        <Skeleton className="mt-5 h-24 w-full" />
      </div>
      <SkeletonList rows={5} height="h-14" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Picks
// ---------------------------------------------------------------------------

export function Picks({
  isLoading,
  isError,
  error,
  retry,
  week,
  pickStateFor,
  onPick,
  onRetry,
  countdownFor,
  weeks,
  onSelectWeek,
}: PicksScreenProps) {
  if (isError && error) return <ErrorState message={error} onRetry={retry} />;
  if (isLoading || !week) return <SkeletonList rows={5} height="h-44" />;

  const remaining = week.requiredPicks - week.myPicksMade;

  return (
    <div className="space-y-5">
      {/* Sticky progress header: the count follows you down the list, so you always know
          how many are left without scrolling back up. */}
      <div className="sticky top-14 z-20 -mx-4 border-b border-line bg-field-900/92 px-4 py-3 backdrop-blur-xl">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight">{week.week.label}</h1>
            <div className="mt-0.5 font-mono text-[11px] uppercase tracking-[0.14em] text-field-500">
              {week.week.slateStatus === 'draft' ? 'Not published' : `${week.games.length} games`}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div
              className={cx(
                'font-mono text-2xl font-semibold tabular-nums leading-none',
                week.allComplete ? 'text-win' : 'text-field-100',
              )}
            >
              {week.myPicksMade}
              <span className="text-field-600">/{week.requiredPicks}</span>
            </div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-field-500">
              Picks made
            </div>
          </div>
        </div>
      </div>

      <WeekStrip weeks={weeks} selectedId={week.week.id} onSelect={onSelectWeek} />

      {week.lockOverrideActive && (
        <InlineNotice tone="warn">
          The commissioner has manually {week.week.picksLockedOverride ? 'locked' : 'unlocked'} this
          week.
        </InlineNotice>
      )}

      {week.week.slateStatus === 'draft' && (
        <EmptyState
          title="This week isn't published yet"
          description="Your commissioner is still building the slate. You'll be able to pick as soon as they publish it."
          icon={<CalendarBlank size={30} />}
        />
      )}

      {week.games.length === 0 && week.week.slateStatus !== 'draft' && (
        <EmptyState title="No games on this slate" description="Nothing was published for this week." />
      )}

      <div className="space-y-3">
        {week.games.map((game, index) => (
          <PickCard
            key={game.id}
            game={game}
            index={index}
            countdown={countdownFor(game.id)}
            pickState={pickStateFor(game.id)}
            onPick={onPick}
            onRetry={onRetry}
          />
        ))}
      </div>

      {week.allComplete && week.games.length > 0 && (
        <div className="taste-pop rounded-card border border-win/30 bg-win/[0.06] p-5 text-center">
          <span className="mx-auto mb-3 grid size-11 place-items-center rounded-full bg-win/15 text-win">
            <Check size={22} weight="bold" />
          </span>
          <p className="text-base font-medium text-win">All {week.requiredPicks} picks are in</p>
          <p className="mt-1 text-sm text-field-400">
            You can change any pick until that game kicks off.
          </p>
        </div>
      )}

      {!week.allComplete && remaining > 0 && week.canPickAny && (
        <p className="pb-2 text-center font-mono text-[12px] text-field-500">
          {remaining} more to go
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Standings
// ---------------------------------------------------------------------------

export function Standings({ isLoading, isError, error, retry, standings }: StandingsScreenProps) {
  if (isError && error) return <ErrorState message={error} onRetry={retry} />;
  if (isLoading || !standings) return <SkeletonList rows={8} height="h-14" />;

  if (standings.rows.length === 0) {
    return <EmptyState title="No standings yet" description="Once a week is graded, the table fills in." icon={<Trophy size={30} />} />;
  }

  // The heading used to say `W-L-P` unconditionally, which stopped being true when every
  // line gained a half point — a push is now unreachable. It is derived rather than simply
  // changed to `W-L`, because rows graded before that rule can still carry one and history
  // is never rewritten to match a later decision.
  const anyPushes = standings.rows.some((row) => row.pushes > 0);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tighter">Standings</h1>
        <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-field-500">
          {standings.season.name} · {standings.gradedWeeks} week
          {standings.gradedWeeks === 1 ? '' : 's'} graded
        </p>
      </div>

      {/*
        Hairline table, no card boxes — the rules do the grouping.

        `table-fixed` with an explicit colgroup, rather than letting the browser size
        columns from their contents. Content-based sizing is what produced the mess this
        replaced: the header `W-L-P` is wider than the data `0-0`, so the columns were laid
        out to fit the headings, collided with each other, and pushed the points column off
        the right edge — on the one screen whose entire purpose is a column of points.

        Fixed widths mean the numbers are always exactly where they were last time, the
        name column takes whatever is left and truncates, and nothing can be squeezed out of
        view. Below `sm` the two least-load-bearing columns step aside; record and points,
        the two anybody actually scans, are always visible.
      */}
      <div className="-mx-3 overflow-x-auto sm:-mx-4">
        <table className="w-full table-fixed border-collapse">
          {/* Widths absorb the new edge padding, so the numbers sit exactly where they did. */}
          <colgroup>
            <col className="w-[44px] sm:w-[48px]" />
            <col />
            <col className="w-[62px]" />
            <col className="hidden w-[52px] sm:table-column" />
            <col className="hidden w-[62px] sm:table-column" />
            <col className="w-[58px] sm:w-[62px]" />
          </colgroup>
          <thead>
            <tr className="border-b border-line">
              {[
                { key: 'rank', label: '', className: 'text-left' },
                { key: 'player', label: 'Player', className: 'text-left' },
                // Abbreviated on a phone so the heading is never wider than its column.
                { key: 'record', label: 'W-L', className: 'text-right sm:hidden' },
                {
                  key: 'record-wide',
                  label: anyPushes ? 'W-L-P' : 'W-L',
                  className: 'hidden text-right sm:table-cell',
                },
                { key: 'pct', label: 'Pct', className: 'hidden text-right sm:table-cell' },
                { key: 'made', label: 'Made', className: 'hidden text-right sm:table-cell' },
                { key: 'pts', label: 'Pts', className: 'text-right' },
              ].map((head) => (
                <th
                  key={head.key}
                  className={cx(
                    'whitespace-nowrap pb-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-field-500',
                    // Edge cells hold the content in off the row's own edges.
                    head.key === 'rank' && 'pl-3 sm:pl-4',
                    head.key === 'pts' && 'pr-3 sm:pr-4',
                    head.className,
                  )}
                >
                  {head.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {standings.rows.map((row, index) => (
              <tr
                key={row.userId}
                className={cx('taste-rise', row.isMe && 'bg-signal/[0.06]')}
                style={{ '--index': index } as React.CSSProperties}
              >
                <td className="py-3 pl-3 pr-1 font-mono text-xs tabular-nums text-field-500 sm:pl-4">
                  {row.tied ? `T${row.rank}` : row.rank}
                </td>
                <td className="py-3 pr-2">
                  <div className="flex items-center gap-2">
                    <Avatar
                      userId={row.userId}
                      displayName={row.displayName}
                      url={row.avatarUrl}
                      size={26}
                      ring={row.isMe}
                    />
                    <span className={cx('truncate text-sm', row.isMe ? 'font-medium text-field-100' : 'text-field-200')}>
                      {row.displayName}
                    </span>
                    {/* The accent ring already marks you, so the badge is desktop-only. */}
                    {row.isMe && (
                      <span className="hidden shrink-0 rounded bg-signal/20 px-1.5 py-0.5 font-mono text-[9px] font-medium tracking-wider text-signal sm:inline">
                        YOU
                      </span>
                    )}
                    {row.isCommissioner && (
                      <span className="hidden shrink-0 font-mono text-[9px] uppercase tracking-wider text-field-600 sm:inline">
                        Commish
                      </span>
                    )}
                  </div>
                  {row.missed > 0 && (
                    <div className="mt-0.5 font-mono text-[10px] text-push">
                      {row.missed} missed
                    </div>
                  )}
                </td>
                <td className="whitespace-nowrap py-3 text-right font-mono text-xs tabular-nums text-field-300">
                  <span className="sm:hidden">
                    {row.wins}-{row.losses}
                  </span>
                  <span className="hidden sm:inline">{formatRecord(row)}</span>
                </td>
                <td className="hidden py-3 text-right font-mono text-xs tabular-nums text-field-400 sm:table-cell">
                  {formatWinPercentage(row)}
                </td>
                <td className="hidden py-3 text-right font-mono text-xs tabular-nums text-field-500 sm:table-cell">
                  {row.picksMade}/{row.picksAvailable}
                </td>
                <td className="py-3 pr-3 text-right font-mono text-base font-semibold tabular-nums text-field-100 sm:pr-4">
                  {formatPoints(row.points)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="font-mono text-[11px] leading-relaxed text-field-600">
        A cover is worth {formatPoints(standings.season.winPoints)}. Every line carries a
        half point, so no game can push. Games pulled by the commissioner are excluded from
        everyone&apos;s totals.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weekly results
// ---------------------------------------------------------------------------

export function WeekResults({
  isLoading,
  isError,
  error,
  retry,
  results,
  weeks,
  selectedWeekId,
  onSelectWeek,
}: WeekResultsScreenProps) {
  if (isError && error) return <ErrorState message={error} onRetry={retry} />;
  if (isLoading || !results) return <SkeletonList rows={6} height="h-12" />;

  const games = results.games.filter((g) => g.status !== 'voided');

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tighter">{results.week.label}</h1>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-field-500">
            {results.finalized ? 'Final' : 'In progress'}
          </p>
        </div>
        {results.weeklyWinners.length > 0 && (
          <div className="shrink-0 text-right">
            <div className="flex items-center justify-end gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-push">
              <Trophy size={13} weight="fill" />
              {results.weeklyWinners.length > 1 ? 'Tied winners' : 'Week winner'}
            </div>
            <div className="mt-1 text-sm font-medium text-field-100">
              {results.weeklyWinners.map((w) => w.displayName).join(', ')}
            </div>
          </div>
        )}
      </div>

      <WeekStrip weeks={weeks} selectedId={selectedWeekId} onSelect={onSelectWeek} />

      {/* Game key so the grid columns are readable. */}
      <Card className="divide-y divide-line">
        {games.map((game) => (
          <div key={game.id} className="flex items-center gap-3 px-4 py-2.5">
            <span className="w-4 shrink-0 font-mono text-[11px] text-field-600">{game.slot}</span>
            <div className="min-w-0 flex-1 truncate text-[13px] text-field-300">
              <span className={cx(game.away.covered === true && 'font-medium text-win')}>
                {game.away.team.abbreviation} {game.away.lineLabel}
              </span>
              <span className="mx-1.5 text-field-600">{game.neutralSite ? 'vs' : 'at'}</span>
              <span className={cx(game.home.covered === true && 'font-medium text-win')}>
                {game.home.team.abbreviation} {game.home.lineLabel}
              </span>
            </div>
            <span className="shrink-0 font-mono text-[12px] tabular-nums text-field-400">
              {game.away.score !== null ? `${game.away.score}–${game.home.score}` : '—'}
            </span>
          </div>
        ))}
      </Card>

      {/*
        The grid. Same sizing story as the standings: the player column absorbs the slack
        and truncates so the per-game cells and the points stay on screen, rather than a
        fixed floor pushing the results themselves out of view on a phone.
      */}
      {/* Same padding story as the standings: highlight full-bleed, content held clear. */}
      <div className="-mx-3 overflow-x-auto sm:-mx-4">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-line">
              <th className="max-w-0 w-full pb-2.5 pl-3 text-left font-mono text-[10px] uppercase tracking-[0.14em] text-field-500 sm:pl-4">
                Player
              </th>
              {games.map((game) => (
                <th
                  key={game.id}
                  className="pb-2.5 text-center font-mono text-[10px] tabular-nums text-field-500"
                >
                  {game.slot}
                </th>
              ))}
              <th className="pb-2.5 pr-3 text-right font-mono text-[10px] uppercase tracking-[0.14em] text-field-500 sm:pr-4">
                Pts
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {results.rows.map((row, index) => (
              <tr
                key={row.userId}
                className={cx('taste-rise', row.isMe && 'bg-signal/[0.06]')}
                style={{ '--index': index } as React.CSSProperties}
              >
                <td className="max-w-0 py-2.5 pl-3 pr-2 sm:pl-4">
                  <div className="flex items-center gap-1.5">
                    <span className={cx('truncate text-[13px]', row.isMe ? 'font-medium text-field-100' : 'text-field-300')}>
                      {row.displayName}
                    </span>
                    {row.isWeeklyWinner && <Trophy size={11} weight="fill" className="shrink-0 text-push" />}
                  </div>
                </td>
                {row.cells.map((cell) => (
                  <td key={cell.slateGameId} className="px-1 py-2.5 text-center">
                    <ResultCell cell={cell} />
                  </td>
                ))}
                <td className="py-2.5 pl-2 pr-3 text-right font-mono text-sm font-semibold tabular-nums text-field-100 sm:pr-4">
                  {formatPoints(row.weekPoints)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!results.finalized && (
        <p className="font-mono text-[11px] text-field-600">
          Picks stay hidden until each game kicks off.
        </p>
      )}
    </div>
  );
}

/**
 * One cell of the results grid.
 *
 * Three states before a game reveals, not one. The grid used to show the same `•••` to
 * everybody, which answered nothing — the question people actually open this screen with
 * on a Saturday morning is who has not picked yet, and a row of identical dots hides that
 * as thoroughly as it hides the picks themselves.
 *
 * So an unrevealed cell says whether a choice exists, and never what it is. `?` is a
 * filled chip, the same shape a revealed pick gets, because something is genuinely in
 * there. `X` is outlined and dashed, because the box is empty.
 *
 * A revealed miss keeps its own `—`. It is a different fact from `X`: one is "hasn't
 * picked yet", the other is "never did", and only the second one costs points.
 */
function ResultCell({ cell }: { cell: ResultCellDto }) {
  if (!cell.revealed) {
    return cell.hasPick ? (
      <span
        title="Picked"
        className="inline-block rounded bg-field-800 px-1.5 py-1 font-mono text-[10px] font-medium text-field-400"
      >
        ?
      </span>
    ) : (
      <span
        title="No pick yet"
        className="inline-block rounded border border-dashed border-line px-1.5 py-1 font-mono text-[10px] font-medium text-field-600"
      >
        X
      </span>
    );
  }
  if (cell.missed || !cell.selectionAbbr) {
    return <span className="font-mono text-[10px] text-field-700">—</span>;
  }
  const tone =
    cell.result === 'win'
      ? 'bg-win/15 text-win'
      : cell.result === 'loss'
        ? 'bg-loss/15 text-loss'
        : cell.result === 'push'
          ? 'bg-push/15 text-push'
          : cell.result === 'void'
            ? 'bg-field-800 text-field-600'
            : 'bg-field-800 text-field-400';

  return (
    <span className={cx('inline-block rounded px-1.5 py-1 font-mono text-[10px] font-medium', tone)}>
      {cell.selectionAbbr}
    </span>
  );
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

export function History({
  isLoading,
  isError,
  error,
  retry,
  weeks,
  onOpenWeek,
  seasonName,
}: HistoryScreenProps) {
  if (isError && error) return <ErrorState message={error} onRetry={retry} />;
  if (isLoading) return <SkeletonList rows={6} height="h-20" />;

  const played = weeks.filter((w) => w.gradedGameCount > 0 || w.slateStatus !== 'draft');

  if (played.length === 0) {
    return (
      <EmptyState
        title="Nothing to look back on yet"
        description="Completed weeks land here with their original spreads, picks and results kept exactly as they were."
        icon={<ClipboardText size={30} />}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tighter">History</h1>
        <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-field-500">
          {seasonName}
        </p>
      </div>

      <div className="space-y-2.5">
        {played.map((week, index) => (
          <button
            key={week.id}
            onClick={() => onOpenWeek(week.id)}
            className="taste-rise block w-full text-left"
            style={{ '--index': index } as React.CSSProperties}
          >
            <Card interactive className="flex items-center gap-4 p-4 active:scale-[0.99]">
              <div className="w-12 shrink-0">
                <div className="font-mono text-lg font-semibold tabular-nums text-field-100">
                  W{week.weekNumber}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-field-600">
                  {week.slateStatus === 'completed' ? 'Final' : week.slateStatus.replace('_', ' ')}
                </div>
              </div>

              <div className="min-w-0 flex-1">
                {week.winners.length > 0 ? (
                  <div className="flex items-center gap-1.5">
                    <Trophy size={12} weight="fill" className="shrink-0 text-push" />
                    <span className="truncate text-[13px] text-field-300">
                      {week.winners.map((w) => w.displayName).join(', ')}
                    </span>
                  </div>
                ) : (
                  <span className="text-[13px] text-field-500">
                    {week.gradedGameCount > 0 ? 'Partly graded' : 'Not graded yet'}
                  </span>
                )}
                <div className="mt-0.5 font-mono text-[11px] text-field-600">
                  {week.gameCount} games
                </div>
              </div>

              <div className="shrink-0 text-right">
                <div className="font-mono text-lg font-semibold tabular-nums text-field-100">
                  {week.myPoints != null ? formatPoints(week.myPoints) : '—'}
                </div>
                <div className="font-mono text-[10px] text-field-600">
                  {formatRecord({ wins: week.myWins, losses: week.myLosses, pushes: week.myPushes })}
                </div>
              </div>

              <CaretRight size={15} className="shrink-0 text-field-600" />
            </Card>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export function Settings({
  session,
  isSaving,
  error,
  notice,
  onSaveProfile,
  onLogout,
  onInstall,
  showIosInstallHint,
  avatar,
  notifications,
}: SettingsScreenProps) {
  const [displayName, setDisplayName] = useState(session?.user.displayName ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  return (
    <div className="max-w-lg space-y-8">
      <h1 className="text-2xl font-semibold tracking-tighter">Settings</h1>

      <section className="space-y-4">
        <SectionLabel>Profile</SectionLabel>
        {error && <InlineNotice tone="error">{error}</InlineNotice>}
        {notice && <InlineNotice tone="success">{notice}</InlineNotice>}

        <AvatarPanel
          {...avatar}
          userId={session?.user.id ?? ''}
          displayName={session?.user.displayName ?? ''}
        />

        <Field label="Display name" hint="Shown in the standings">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Button
          onClick={() => onSaveProfile({ displayName })}
          loading={isSaving}
          disabled={!displayName || displayName === session?.user.displayName}
        >
          Save name
        </Button>
      </section>

      <section className="space-y-4 border-t border-line pt-8">
        <SectionLabel>Password</SectionLabel>
        <Field label="Current password">
          <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password" />
        </Field>
        <Field label="New password" hint="At least 8 characters">
          <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" />
        </Field>
        <Button
          variant="outline"
          onClick={() => {
            onSaveProfile({ currentPassword, newPassword });
            setCurrentPassword('');
            setNewPassword('');
          }}
          disabled={!currentPassword || newPassword.length < 10}
        >
          Change password
        </Button>
      </section>

      <NotificationsPanel {...notifications} />

      <section className="space-y-4 border-t border-line pt-8">
        <SectionLabel>Install</SectionLabel>
        {onInstall ? (
          <>
            <p className="text-sm leading-relaxed text-field-400">
              Add Pick&apos;em to your home screen so it opens like an app.
            </p>
            <Button onClick={onInstall} variant="outline">
              <DeviceMobile size={17} />
              Install app
            </Button>
          </>
        ) : showIosInstallHint ? (
          <div className="rounded-xl border border-line p-4">
            <p className="text-sm font-medium text-field-100">Add to Home Screen</p>
            <p className="mt-1 text-[12px] text-field-500">
              Then launch it from there, so it opens like an app.
            </p>
            <div className="mt-3">
              <IosInstallSteps />
            </div>
          </div>
        ) : (
          <p className="text-sm text-field-400">This app is already installed.</p>
        )}
      </section>

      <section className="border-t border-line pt-8">
        <div className="mb-4 font-mono text-[11px] text-field-600">{session?.user.email}</div>
        <Button variant="danger" onClick={onLogout}>
          Sign out
        </Button>
      </section>
    </div>
  );
}


/**
 * Profile picture.
 *
 * One control that does the obvious thing: tap the picture to change it. The hidden file
 * input is the standard trick for styling a file picker at all — the native control cannot
 * be styled and would be the ugliest thing on the screen.
 *
 * The pending preview is drawn from the resized image the browser already produced, so the
 * new picture appears the instant it is chosen rather than after the round trip. Nothing is
 * claimed to be saved until it is: the spinner stays until the server confirms.
 */
function AvatarPanel({
  url,
  pendingUrl,
  isSaving,
  error,
  onChoose,
  onRemove,
  userId,
  displayName,
}: AvatarPanelProps & { userId: string; displayName: string }) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="relative rounded-full transition-transform active:scale-95"
        aria-label="Change profile picture"
      >
        {pendingUrl ? (
          <img src={pendingUrl} alt="" aria-hidden className="size-16 rounded-full object-cover" />
        ) : (
          <Avatar userId={userId} displayName={displayName} url={url} size={64} />
        )}
        <span className="absolute -bottom-0.5 -right-0.5 grid size-6 place-items-center rounded-full border-2 border-field-900 bg-signal text-ink">
          {isSaving ? (
            <ArrowClockwise size={12} weight="bold" className="animate-spin" />
          ) : (
            <Camera size={12} weight="fill" />
          )}
        </span>
      </button>

      <div className="min-w-0 flex-1">
        <div className="text-sm text-field-200">Profile picture</div>
        <div className="mt-0.5 text-[12px] text-field-500">
          Shown on the leaderboard. Squared and shrunk on your device before it is sent.
        </div>
        {url && !isSaving && (
          <button
            type="button"
            onClick={onRemove}
            className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-field-500 hover:text-loss"
          >
            Remove
          </button>
        )}
        {error && <div className="mt-1.5 text-[12px] text-loss">{error}</div>}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onChoose(file);
          // Cleared so picking the same file twice still fires a change event — otherwise a
          // failed upload could not be retried without choosing something else first.
          e.target.value = '';
        }}
      />
    </div>
  );
}

/**
 * Notifications.
 *
 * Four states, and they are genuinely different problems rather than shades of one: the
 * browser cannot do this at all; it could if the app were installed; the server has no
 * keys; or permission was refused and only the user can undo that. A single greyed-out
 * toggle would collapse all four into "it doesn't work", which helps nobody.
 */
function NotificationsPanel({
  support,
  configured,
  permission,
  subscribed,
  isBusy,
  error,
  notice,
  reminderHours,
  finalCallHours,
  onEnable,
  onDisable,
  onSendTest,
  devices,
  devicesLoaded,
  removingDeviceId,
  onRemoveDevice,
}: NotificationsPanelProps) {
  return (
    <section className="space-y-4 border-t border-line pt-8">
      <SectionLabel>Notifications</SectionLabel>

      <p className="text-sm leading-relaxed text-field-400">
        A nudge when a new slate goes up, a reminder {formatHours(reminderHours)} before the
        first kickoff if you still have picks to make, and a last call{' '}
        {formatHours(finalCallHours)} out. Nothing else.
      </p>

      {support === 'needs-install' ? (
        <InlineNotice tone="info">
          On an iPhone, notifications only work once the app is on your home screen. Add it
          using the steps under Install below, open it from there, then come back to this
          screen.
        </InlineNotice>
      ) : support === 'unsupported' ? (
        <InlineNotice tone="info">
          This browser cannot receive push notifications. Chrome, Edge, Firefox and Safari
          all can.
        </InlineNotice>
      ) : !configured ? (
        <InlineNotice tone="warn">
          Notifications are not set up on this server yet — it needs a VAPID key pair.
        </InlineNotice>
      ) : permission === 'denied' && !subscribed ? (
        <InlineNotice tone="warn">
          Notifications are blocked for this site. The app cannot ask again — you will need
          to allow them in your browser&apos;s site settings first.
        </InlineNotice>
      ) : subscribed ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm text-win">
            <CheckCircle size={16} weight="fill" />
            On for this device
          </span>
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" size="sm" onClick={onSendTest} loading={isBusy}>
              Send a test
            </Button>
            <Button variant="outline" size="sm" onClick={onDisable} disabled={isBusy}>
              Turn off
            </Button>
          </div>
        </div>
      ) : (
        <Button onClick={onEnable} loading={isBusy}>
          <BellRinging size={17} weight="fill" />
          Turn on notifications
        </Button>
      )}

      {notice && <InlineNotice tone="success">{notice}</InlineNotice>}
      {error && <InlineNotice tone="error">{error}</InlineNotice>}

      {devicesLoaded && devices.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-field-500">
              Devices
            </div>
            <div className="font-mono text-[11px] tabular-nums text-field-600">
              {devices.length}
            </div>
          </div>

          {/*
            Push subscriptions accumulate quietly — a browser you signed into once, a phone
            you replaced. "Turn off" only ever reaches the device in your hand, so without
            this list the others keep buzzing forever with nothing to press.
          */}
          <div className="divide-y divide-line border-y border-line">
            {devices.map((device) => (
              <div key={device.id} className="flex items-center gap-3 py-2.5">
                <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-field-800 text-field-400">
                  <DeviceMobile size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 truncate text-[13px] text-field-200">
                    {device.label}
                    {device.isThisDevice && (
                      <span className="shrink-0 rounded bg-signal/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-signal">
                        This one
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 font-mono text-[11px] text-field-600">
                    {device.lastSeenAt
                      ? `Last notified ${formatDay(device.lastSeenAt)}`
                      : `Added ${formatDay(device.addedAt)} · nothing sent yet`}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveDevice(device.id)}
                  disabled={removingDeviceId !== null}
                  className="shrink-0 rounded-md px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-field-500 transition-colors hover:text-loss disabled:opacity-40"
                >
                  {removingDeviceId === device.id ? 'Removing' : 'Remove'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/** `12 Aug` — enough to recognise, without pretending to a precision nobody needs. */
function formatDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'recently';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** "24 hours" / "90 minutes" — whichever reads naturally at that magnitude. */
function formatHours(hours: number): string {
  if (hours >= 1) {
    const rounded = Math.round(hours);
    return `${rounded} hour${rounded === 1 ? '' : 's'}`;
  }
  const minutes = Math.round(hours * 60);
  return `${minutes} minutes`;
}

// ---------------------------------------------------------------------------
// Not found
// ---------------------------------------------------------------------------

export function NotFound({ homeHref }: { homeHref: string }) {
  return (
    <div className="grid min-h-[60vh] place-items-center">
      <div className="text-center">
        <div className="font-mono text-6xl font-semibold tracking-tighter text-field-800">404</div>
        <p className="mt-4 text-lg font-medium text-field-100">That page isn&apos;t here</p>
        <p className="mt-1.5 text-sm text-field-400">The link may be out of date.</p>
        <Link to={homeHref} className="mt-6 inline-block">
          <Button variant="outline">Back to the dashboard</Button>
        </Link>
      </div>
    </div>
  );
}

/** Re-exported so the variant index can present a single surface. */
export { TeamMark };
